import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { useRouteMatch } from "react-router-dom";
import { Box, Button, Typography } from "@material-ui/core";
import { add, startOfDay, sub } from "date-fns";
import { Appointment, Query, QueryGetMyAppointmentsArgs } from "../../generated/nest-graphql";
import { GET_MY_APPOINTMENTS } from "../../graphql/queries/getMyAppointments";
import { CloseJobConfirmDialog } from "./CloseJobConfirmDialog";

// Job statuses worth nudging the tech to close (mirrors the backend canClose gate).
const CLOSEABLE_STATUSES = ["Open: In-Progress", "Open: Ready For Billing"];

const needsClosing = (status?: string | null): boolean => !!status && CLOSEABLE_STATUSES.includes(status);

type OpenJob = {
  id: string;
  jobNumber?: string | null;
  status?: string | null;
  jobStartedAt?: string | null;
};

/**
 * Persistent reminder banner (NUB-300). Whenever the technician has an open job
 * and is NOT currently viewing that job's page, show a banner with a Close Job
 * button so jobs don't get left open. Tapping opens the confirmation dialog.
 */
export const CloseJobBanner: React.FC = () => {
  const { startRange, endRange } = useMemo(() => {
    const today = startOfDay(new Date());
    return { startRange: sub(today, { days: 2 }), endRange: add(today, { days: 1 }) };
  }, []);

  const { data } = useQuery<Query, QueryGetMyAppointmentsArgs>(GET_MY_APPOINTMENTS, {
    variables: { startRange, endRange },
    fetchPolicy: "cache-and-network",
    pollInterval: 60000,
  });

  // Are we currently on a job's own page? If so, that job is excluded from the banner.
  const jobRouteMatch = useRouteMatch<{ id: string }>("/jobs/:id");
  const currentJobId = jobRouteMatch?.params?.id;

  const [dialogJob, setDialogJob] = useState<OpenJob | null>(null);

  const openJobs = useMemo<OpenJob[]>(() => {
    const appointments = (data?.getMyAppointments ?? []) as Appointment[];
    const byId = new Map<string, OpenJob>();
    for (const appt of appointments) {
      const job = appt?.job;
      if (!job?.id || !needsClosing(job.status)) continue;
      if (job.id === currentJobId) continue;
      byId.set(job.id, {
        id: job.id,
        jobNumber: job.jobNumber,
        status: job.status,
        jobStartedAt: (job as any).jobStartedAt,
      });
    }
    return Array.from(byId.values());
  }, [data, currentJobId]);

  if (openJobs.length === 0) return null;

  // Most common case is a single active job; if there are several, surface the first
  // and note the remaining count.
  const [job, ...rest] = openJobs;
  const label =
    rest.length > 0 ? `You have ${openJobs.length} open jobs` : `Job ${job.jobNumber ?? ""} is still open`.trim();

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={1}
        style={{ backgroundColor: "#b71c1c", color: "#fff" }}
      >
        <Typography variant="body2">{label}</Typography>
        <Button size="small" variant="contained" color="default" onClick={() => setDialogJob(job)}>
          Close Job
        </Button>
      </Box>
      {dialogJob && (
        <CloseJobConfirmDialog
          open={!!dialogJob}
          onClose={() => setDialogJob(null)}
          jobId={dialogJob.id}
          jobStartedAt={dialogJob.jobStartedAt}
        />
      )}
    </>
  );
};
